import { useCallback, useMemo, useRef } from 'react';

import { useTrackSavedFilterUpdated } from '@air/analytics';
import { type SavedFilter } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { type ModalFormData, SavedFilterModal } from '~/components/SavedFilter/components/SavedFilterModal';
import { DeleteSavedFilterModal } from '~/components/SavedFilter/DeleteSavedFilterModal';
import { useUpdateSavedFilter } from '~/components/SavedFilter/shared/hooks/useUpdateSavedFilter';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { canDeleteSavedFilters } from '~/utils/permissions/workspacePermissions';

export type EditSavedFiltersModalProps = {
  savedFilter: SavedFilter;
} & AirModalProps;

export const EditSavedFilterModal = ({ onClose, savedFilter }: EditSavedFiltersModalProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { updateSavedFilter } = useUpdateSavedFilter();
  const [showDeleteSavedFilterModal] = useAirModal(DeleteSavedFilterModal);
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canDeleteSavedFilter = canDeleteSavedFilters(permissions);
  const { trackSavedFilterUpdated } = useTrackSavedFilterUpdated();
  const { showToast } = useToasts();
  // isPending updates on the next render, so the ref blocks a second click in the same turn.
  // The modal then disables the button and Enter from isSubmitting.
  const submitLockRef = useRef<boolean>();

  const onApplyClick = useCallback(
    (modalFormData: ModalFormData) => {
      if (submitLockRef.current || !currentWorkspace?.id) {
        return;
      }

      submitLockRef.current = true;

      updateSavedFilter.mutate(
        {
          id: savedFilter.id,
          ...modalFormData,
          criteria: {
            ...savedFilter.criteria,
            workspaceId: currentWorkspace.id,
          },
        },
        {
          onSuccess: () => {
            if (!currentWorkspaceMember) {
              return;
            }

            trackSavedFilterUpdated({
              currentUser: currentWorkspaceMember.accountId || '',
              role: currentWorkspaceMember?.role?.type || '',
              createdBy: savedFilter.createdBy,
            });

            onClose();
          },
          onError: () => {
            showToast('Failed to update saved search', { color: 'red' });
          },
          onSettled: () => {
            submitLockRef.current = false;
          },
        },
      );
    },
    [
      updateSavedFilter,
      savedFilter.id,
      savedFilter.criteria,
      savedFilter.createdBy,
      currentWorkspace?.id,
      currentWorkspaceMember,
      trackSavedFilterUpdated,
      onClose,
      showToast,
    ],
  );

  const onDelete = useCallback(() => {
    showDeleteSavedFilterModal({ savedFilter, onDelete: () => onClose() });
  }, [onClose, savedFilter, showDeleteSavedFilterModal]);

  const tertiaryCTA = useMemo(() => {
    return canDeleteSavedFilter ? (
      <Button appearance="ghost" color="red" size="large" onClick={onDelete}>
        Delete
      </Button>
    ) : undefined;
  }, [canDeleteSavedFilter, onDelete]);

  return (
    <SavedFilterModal
      isEditing
      isSubmitReady={!!currentWorkspace?.id}
      isSubmitting={updateSavedFilter.isPending}
      modalLabel="Update saved search"
      onClose={onClose}
      onSubmit={onApplyClick}
      primaryCTACopy="Update"
      savedFilter={savedFilter}
      tertiaryCTA={tertiaryCTA}
    />
  );
};
